import { apiClient, apiClientPaginated } from '@/lib/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface ModifierOption {
  id: string;
  name: string;
  price_adjustment: number;
  sort_order: number;
}

export interface ModifierGroup {
  id: string;
  location_id: string;
  name: string;
  required: boolean;
  max_selections: number | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  options: ModifierOption[];
  product_ids: string[];
}

interface ModifierListResponse {
  data: ModifierGroup[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

// Tenant-/location-wide KPI aggregate (GitHub issue #285). Computed server-side
// across every modifier group matching the current filters so the KPI cards stay
// constant as you paginate, instead of being reduced from the current 20-row
// page slice. `avg_options` is a raw (unrounded) average options-per-group.
export interface ModifierStats {
  total_groups: number;
  required_groups: number;
  avg_options: number;
}

interface CreateModifierRequest {
  name: string;
  location_id: string;
  options: { name: string; price_adjustment: number }[];
  required?: boolean;
  max_selections?: number;
  product_ids?: string[];
}

interface UpdateModifierRequest {
  name?: string;
  required?: boolean;
  max_selections?: number;
  options?: { name: string; price_adjustment: number }[];
  product_ids?: string[];
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function buildQuery(params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== '',
  );
  if (entries.length === 0) return '';
  return '?' + entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&');
}

// ─── Service ────────────────────────────────────────────────────────────────

export const modifierService = {
  async list(params: {
    location_id?: string;
    product_id?: string;
    search?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<ModifierListResponse> {
    return apiClientPaginated<ModifierGroup[]>(`/modifiers${buildQuery({
      location_id: params.location_id,
      product_id: params.product_id,
      search: params.search,
      page: params.page,
      limit: params.limit,
    })}`);
  },

  async getStats(params: {
    location_id?: string;
    product_id?: string;
  } = {}): Promise<ModifierStats> {
    return apiClient<ModifierStats>(`/modifiers/stats${buildQuery({
      location_id: params.location_id,
      product_id: params.product_id,
    })}`);
  },

  async create(dto: CreateModifierRequest): Promise<ModifierGroup> {
    return apiClient<ModifierGroup>('/modifiers', { method: 'POST', body: dto });
  },

  async update(id: string, dto: UpdateModifierRequest): Promise<ModifierGroup> {
    return apiClient<ModifierGroup>(`/modifiers/${id}`, { method: 'PATCH', body: dto });
  },

  async deactivate(id: string): Promise<unknown> {
    return apiClient(`/modifiers/${id}`, { method: 'DELETE' });
  },
};
